import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
	useContractTypes,
	useCreateContractType,
	useUpdateContractType,
	useDeleteContractType,
} from '@/hooks/useMaster'
import { UserRole } from '@kfolks/types'
import { roleAtLeast } from '@/auth'
import { useAuth } from '@/hooks/useAuth'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'

interface EditingRow {
	id: string
	code: string
	label: string
	sortOrder: number
}

interface SortableRowProps {
	row: {
		contractTypeId: string
		code: string
		label: string
		sortOrder: number
		isActive: boolean
	}
	isEditing: boolean
	editingData: EditingRow | null
	onEdit: (row: EditingRow) => void
	onSaveEdit: () => void
	onCancelEdit: () => void
	onDelete: (id: string) => void
	setEditingData: (data: EditingRow | null) => void
	updatePending: boolean
	deletePending: boolean
	isSuperAdmin: boolean
}

function SortableRow({
	row,
	isEditing,
	editingData,
	onEdit,
	onSaveEdit,
	onCancelEdit,
	onDelete,
	setEditingData,
	updatePending,
	deletePending,
	isSuperAdmin,
}: SortableRowProps) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: row.contractTypeId,
	})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	}

	return (
		<TableRow ref={setNodeRef} style={style}>
			<TableCell className="w-12">
				<div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
					<GripVertical className="h-5 w-5 text-gray-400" />
				</div>
			</TableCell>
			<TableCell className="font-mono text-sm">
				{isEditing ? (
					<Input
						value={editingData?.code ?? ''}
						onChange={(e) => setEditingData(editingData ? { ...editingData, code: e.target.value } : null)}
						className="font-mono"
					/>
				) : (
					row.code
				)}
			</TableCell>
			<TableCell>
				{isEditing ? (
					<Input
						value={editingData?.label ?? ''}
						onChange={(e) => setEditingData(editingData ? { ...editingData, label: e.target.value } : null)}
					/>
				) : (
					row.label
				)}
			</TableCell>
			<TableCell>{row.isActive ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
			<TableCell className="text-right">
				{isSuperAdmin && (
					<>
						{isEditing ? (
							<div className="flex items-center justify-end gap-1">
								<Button onClick={onSaveEdit} size="sm" disabled={updatePending} className="h-7 px-2 text-xs">
									Save
								</Button>
								<Button onClick={onCancelEdit} size="sm" variant="outline" className="h-7 px-2 text-xs">
									Cancel
								</Button>
							</div>
						) : (
							<div className="flex items-center justify-end gap-1">
								<Button
									onClick={() =>
										onEdit({
											id: row.contractTypeId,
											code: row.code,
											label: row.label,
											sortOrder: row.sortOrder,
										})
									}
									size="sm"
									variant="outline"
									className="h-7 px-2 text-xs"
								>
									Edit
								</Button>
								<Button
									onClick={() => onDelete(row.contractTypeId)}
									size="sm"
									variant="destructive"
									disabled={deletePending}
									className="h-7 px-2 text-xs"
								>
									Delete
								</Button>
							</div>
						)}
					</>
				)}
			</TableCell>
		</TableRow>
	)
}

export function ContractTypesTable() {
	const { user } = useAuth()
	const isSuperAdmin = roleAtLeast(user?.role, UserRole.SUPER_ADMIN)

	const queryClient = useQueryClient()
	const { data: contractTypes = [], isLoading, error } = useContractTypes()
	const createMutation = useCreateContractType()
	const updateMutation = useUpdateContractType()
	const deleteMutation = useDeleteContractType()

	const [editingId, setEditingId] = useState<string | null>(null)
	const [editingData, setEditingData] = useState<EditingRow | null>(null)
	const [isAdding, setIsAdding] = useState(false)
	const [newRow, setNewRow] = useState({ code: '', label: '', sortOrder: 0 })

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	)

	const handleEdit = (row: EditingRow) => {
		setEditingId(row.id)
		setEditingData(row)
	}

	const handleCancelEdit = () => {
		setEditingId(null)
		setEditingData(null)
	}

	const handleSaveEdit = async () => {
		if (!editingData || !editingId) return

		try {
			await updateMutation.mutateAsync({
				id: editingId,
				data: {
					code: editingData.code,
					label: editingData.label,
					sortOrder: editingData.sortOrder,
				},
			})
			setEditingId(null)
			setEditingData(null)
		} catch {}
	}

	const handleDelete = async (id: string) => {
		if (!confirm('Are you sure you want to delete this contract type?')) return

		try {
			await deleteMutation.mutateAsync(id)
		} catch {}
	}

	const handleAddNew = () => {
		setIsAdding(true)
		setNewRow({ code: '', label: '', sortOrder: contractTypes.length })
	}

	const handleCancelAdd = () => {
		setIsAdding(false)
		setNewRow({ code: '', label: '', sortOrder: 0 })
	}

	const handleSaveNew = async () => {
		if (!newRow.code || !newRow.label) {
			alert('Code and Label are required')
			return
		}

		try {
			await createMutation.mutateAsync({
				code: newRow.code,
				label: newRow.label,
				sortOrder: newRow.sortOrder,
			})
			setIsAdding(false)
			setNewRow({ code: '', label: '', sortOrder: 0 })
		} catch {}
	}

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event

		if (!over || active.id === over.id) return

		const sortedData = [...contractTypes].sort((a, b) => a.sortOrder - b.sortOrder)
		const oldIndex = sortedData.findIndex((item) => item.contractTypeId === active.id)
		const newIndex = sortedData.findIndex((item) => item.contractTypeId === over.id)

		if (oldIndex === -1 || newIndex === -1) return

		const reorderedItems = [...sortedData]
		const [movedItem] = reorderedItems.splice(oldIndex, 1)
		reorderedItems.splice(newIndex, 0, movedItem)

		const updatedItems = reorderedItems.map((item, index) => ({
			...item,
			sortOrder: index,
		}))

		// Optimistic update
		queryClient.setQueryData(['contract-types'], updatedItems)

		try {
			await Promise.all(
				updatedItems.map((item) =>
					updateMutation.mutateAsync({
						id: item.contractTypeId,
						data: { sortOrder: item.sortOrder },
					}),
				),
			)
		} catch {
			await queryClient.invalidateQueries({ queryKey: ['contract-types'] })
		}
	}

	if (isLoading) {
		return <div className="text-center py-8">Loading contract types...</div>
	}

	if (error) {
		return <div className="text-center py-8 text-red-600">Error loading contract types: {error.message}</div>
	}

	const sortedData = [...contractTypes].sort((a, b) => a.sortOrder - b.sortOrder)

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<p className="text-sm text-gray-600">{contractTypes.length} contract types</p>
				{isSuperAdmin && (
					<Button onClick={handleAddNew} disabled={isAdding} size="sm">
						Add Contract Type
					</Button>
				)}
			</div>

			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => void handleDragEnd(event)}>
				<div className="border rounded-lg">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-12"></TableHead>
								<TableHead>Code</TableHead>
								<TableHead>Label</TableHead>
								<TableHead>Status</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{isAdding && (
								<TableRow>
									<TableCell></TableCell>
									<TableCell>
										<Input
											value={newRow.code}
											onChange={(e) => setNewRow({ ...newRow, code: e.target.value })}
											placeholder="CODE"
											className="font-mono"
										/>
									</TableCell>
									<TableCell>
										<Input
											value={newRow.label}
											onChange={(e) => setNewRow({ ...newRow, label: e.target.value })}
											placeholder="Display Label"
										/>
									</TableCell>
									<TableCell>
										<Badge>Active</Badge>
									</TableCell>
									<TableCell className="text-right">
										<div className="flex items-center justify-end gap-1">
											<Button
												onClick={() => void handleSaveNew()}
												size="sm"
												disabled={createMutation.isPending}
												className="h-7 px-2 text-xs"
											>
												Save
											</Button>
											<Button onClick={handleCancelAdd} size="sm" variant="outline" className="h-7 px-2 text-xs">
												Cancel
											</Button>
										</div>
									</TableCell>
								</TableRow>
							)}

							<SortableContext
								items={sortedData.map((item) => item.contractTypeId)}
								strategy={verticalListSortingStrategy}
							>
								{sortedData.map((row) => {
									const isEditing = editingId === row.contractTypeId

									return (
										<SortableRow
											key={row.contractTypeId}
											row={row}
											isEditing={isEditing}
											editingData={editingData}
											onEdit={handleEdit}
											onSaveEdit={() => void handleSaveEdit()}
											onCancelEdit={handleCancelEdit}
											onDelete={(id: string) => void handleDelete(id)}
											setEditingData={setEditingData}
											updatePending={updateMutation.isPending}
											deletePending={deleteMutation.isPending}
											isSuperAdmin={isSuperAdmin}
										/>
									)
								})}
							</SortableContext>

							{!isAdding && sortedData.length === 0 && (
								<TableRow>
									<TableCell colSpan={5} className="text-center py-8 text-gray-500">
										No contract types found. Click "Add Contract Type" to create one.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</DndContext>
		</div>
	)
}
